import { WButton } from "@/components/button/WButton.tsx";
import { WTooltipButton } from "@/components/button/WToolButton.tsx";
import { useApiClient } from "@/modules/api/apiClient.store.ts";
import { getIsReport, type CommonInvoice } from "@/modules/billing/invoice/invoiceLogic.tsx";
import { getTransTextWithDefault } from "@/modules/l10n/getTransText.ts";
import { useThemeProps } from "@/modules/theme/useTheme.ts";
import { downloadBlob } from "@/utils/downloadBlob.tsx";
import { useLingui } from "@lingui/react/macro";
import { notNull } from "@warrenio/utils/notNull";

interface DownloadPdfProps {
    item: CommonInvoice;
    isAdmin?: boolean;
    inTable?: boolean;
}

export function DownloadPdf({ item, isAdmin = false, inTable = false }: DownloadPdfProps) {
    const { t, i18n } = useLingui();
    const api = useApiClient();
    const { invoiceAltTranslations } = useThemeProps();

    const { document_number, id } = item;

    const isReport = getIsReport(item);

    function getPdfName() {
        const invoicePrefix = getTransTextWithDefault(i18n, invoiceAltTranslations?.pdfFilePrefix, t`Invoice`);
        const prefix = isReport ? t`Report` : invoicePrefix;
        return `${prefix} ${document_number}.pdf`;
    }

    async function downloadPdf() {
        const response = await api.GET(isAdmin ? "/payment/admin/invoice/pdf" : "/payment/invoice/pdf", {
            params: { query: { invoice_id: Number(id) } },
            parseAs: "blob",
        });

        downloadBlob(notNull(response.data, "PDF data"), getPdfName());
    }

    const title = t`Download PDF`;
    return inTable ? (
        <WTooltipButton
            title={title}
            variant="ghost"
            size="xs"
            icon="jp-icon-download"
            color="primary"
            action={downloadPdf}
        />
    ) : (
        <WButton color="primary" size="bar" variant="ghost" action={downloadPdf} icon="jp-icon-download">
            {title}
        </WButton>
    );
}
